import React, { useState } from "react";
import { Box, Typography, Card, useTheme, useMediaQuery, Modal, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Eye, X, ChevronDown } from "lucide-react";
import labReportImage from "../assets/Gemini_Generated_Image_yirgsjyirgsjyirg (1).png";

// Import Allergy Report images
import allergy1 from "../assets/Allergy Report/Allergy 1.png";
import allergy2 from "../assets/Allergy Report/Allergy2.png";
import allergy3 from "../assets/Allergy Report/Allergy 3 (1).png";
import allergy4 from "../assets/Allergy Report/Page 42.png";

// Import Food Intolerance Report images
import foodIntolerance1 from "../assets/Food Intolerance Report/Food intolerance 1.png";
import foodIntolerance2 from "../assets/Food Intolerance Report/Food intolerance 2.png";
import foodIntolerance3 from "../assets/Food Intolerance Report/Food intolerance 3.png";

// Import Pre Natal Report images
import preNatal1 from "../assets/Pre Natal Report/Prenatal 1.png";
import preNatal2 from "../assets/Pre Natal Report/Prenatal 2.png";

// Image arrays for preview modals
const allergyImages = [allergy1, allergy2, allergy3, allergy4];
const foodIntoleranceImages = [foodIntolerance1, foodIntolerance2, foodIntolerance3];
const preNatalImages = [preNatal1, preNatal2];

/**
 * New Launches Section
 * 
 * Asymmetrical bento-style grid with alternating card widths
 * Row 1: Wide left (60-65%), Narrow right (35-40%)
 * Row 2: Narrow left (35-40%), Wide right (60-65%)
 */
// Preview Button Styled Component
const PreviewButton = styled(Box)(({ theme, isDark }) => ({
  background: isDark
    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.25) 100%)"
    : "linear-gradient(135deg, #4674D9 0%, #6E91EB 100%)",
  color: "#FFFFFF",
  borderRadius: "50px",
  padding: "10px 20px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  fontSize: "14px",
  fontWeight: 600,
  fontFamily: "Inter, sans-serif",
  boxShadow: isDark
    ? "0 4px 16px rgba(255, 255, 255, 0.1)"
    : "0 4px 16px rgba(70, 116, 217, 0.3)",
  border: isDark ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: isDark
      ? "0 6px 20px rgba(255, 255, 255, 0.15)"
      : "0 8px 24px rgba(70, 116, 217, 0.4)",
    background: isDark
      ? "linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.3) 100%)"
      : "linear-gradient(135deg, #3563C8 0%, #5D80DA 100%)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "8px 16px",
    fontSize: "13px",
    gap: "6px",
  },
}));

const NewLaunchesSection = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  const [currentTitle, setCurrentTitle] = useState("");

  const handleOpenModal = (images, title) => {
    setCurrentImages(images);
    setCurrentTitle(title);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentImages([]);
    setCurrentTitle("");
  };

  const handleExternalLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const launches = [
    {
      title: "Lab Report Analyzer",
      description: "AI-powered tool to interpret lab results quickly and accurately.",
      features: [
        "Instant report analysis",
        "Flagged abnormalities",
        "Smart interpretation with insights",
      ],
      theme: "dark", // Dark blue background with white text
      width: "wide", // Row 1, left card - wide
      image: labReportImage,
      previewImages: null, // No preview for this one
      externalLink: "https://www.niro.health/", // External link for preview button
    },
    {
      title: "Allergy Report",
      description: "Comprehensive insights to identify allergen triggers and sensitivities.",
      features: [
        "Instant report analysis",
        "Flagged abnormalities",
        "Smart interpretation with insights",
      ],
      theme: "light", // White background with dark gray text
      width: "narrow", // Row 1, right card - narrow
      image: allergy1, // Use first image from folder
      previewImages: allergyImages, // All images for preview
      externalLink: null,
    },
    {
      title: "Food Intolerance Report",
      description: "Easy-to-understand food analysis that may cause digestive discomfort.",
      features: [
        "Categorized intolerance levels",
        "Gut-friendly food suggestions",
        "Actionable dietary guidance",
      ],
      theme: "light", // White background with dark gray text
      width: "narrow", // Row 2, left card - narrow
      image: foodIntolerance1, // Use first image from folder
      previewImages: foodIntoleranceImages, // All images for preview
      externalLink: null,
    },
    {
      title: "Pre Natal Report",
      description: "Easy-to-understand insights on maternal and fetal health indicators.",
      features: [
        "Clear categorization of nutritional and pregnancy-risk levels",
        "Personalized pregnancy-safe nutrition and lifestyle suggestions",
        "Actionable guidance for each trimester to support healthy development",
      ],
      theme: "dark", // Dark blue background with white text
      width: "wide", // Row 2, right card - wide
      image: preNatal1, // Use first image from folder
      previewImages: preNatalImages, // All images for preview
      externalLink: null,
    },
  ];

  return (
    <Box
      id="smart-report-bottom-section"
      sx={{
        width: "100%",
        padding: {
          xs: "60px 16px",
          sm: "80px 24px",
          md: "100px 48px",
        },
        backgroundColor: "transparent",
      }}
    >
      <Box
        sx={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            textAlign: "center",
            marginBottom: { xs: "40px", md: "60px" },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "28px", sm: "36px", md: "42px" },
              fontWeight: 700,
              fontFamily: "'Poppins', sans-serif",
              color: "#022674",
            }}
          >
            Our New Launches
          </Typography>
        </Box>

        {/* Asymmetrical Bento-Style Grid */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: "32px", md: "48px" },
          }}
        >
          {/* Row 1: Wide left, Narrow right */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.65fr 1fr" }, // ~62% / ~38%
              gap: { xs: "32px", md: "48px" },
            }}
          >
            {launches.slice(0, 2).map((launch, index) => {
              const isDarkTheme = launch.theme === "dark";
              
              return (
                <Card
                  key={index}
                  sx={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    backgroundColor: isDarkTheme ? "#022674" : "white",
                    display: "flex",
                    flexDirection: "column",
                    paddingBottom: 0,
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 48px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  {/* Card Content - Top */}
                  <Box
                    sx={{
                      padding: { xs: "24px", md: "32px" },
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      ...(!isDarkTheme && {
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='watermark' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M0 0h100v100H0z' fill='%23f8f9fa'/%3E%3Cpath d='M0 50 Q25 40, 50 50 T100 50' stroke='%23e2e8f0' stroke-width='0.5' fill='none' opacity='0.3'/%3E%3Cpath d='M0 30 Q25 20, 50 30 T100 30' stroke='%23e2e8f0' stroke-width='0.5' fill='none' opacity='0.2'/%3E%3Cpath d='M0 70 Q25 60, 50 70 T100 70' stroke='%23e2e8f0' stroke-width='0.5' fill='none' opacity='0.2'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23watermark)'/%3E%3C/svg%3E")`,
                          backgroundSize: "200px 200px",
                          opacity: 0.4,
                          pointerEvents: "none",
                          zIndex: 0,
                        },
                      }),
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "22px", md: "28px" },
                        fontWeight: 700,
                        fontFamily: "'Poppins', sans-serif",
                        color: isDarkTheme ? "#ffffff" : "#1e293b",
                        marginBottom: "12px",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {launch.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "14px", md: "16px" },
                        color: isDarkTheme ? "#ffffff" : "#64748b",
                        lineHeight: 1.6,
                        marginBottom: "24px",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {launch.description}
                    </Typography>

                    {/* Features List */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        position: "relative",
                        zIndex: 1,
                        marginBottom: (launch.previewImages || launch.externalLink) ? "20px" : 0,
                      }}
                    >
                      {launch.features.map((feature, featureIndex) => (
                        <Box
                          key={featureIndex}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "12px",
                          }}
                        >
                          {/* Light blue circular dot */}
                          <Box
                            sx={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: "#5BA3FF",
                              marginTop: "6px",
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: "14px", md: "16px" },
                              color: isDarkTheme ? "#ffffff" : "#1e293b",
                              lineHeight: 1.6,
                              fontWeight: 500,
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Preview Button - Show if previewImages or externalLink exist */}
                    {(launch.previewImages && launch.previewImages.length > 0) || launch.externalLink ? (
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          marginTop: "auto",
                          marginBottom: "16px",
                        }}
                      >
                        <PreviewButton
                          isDark={isDarkTheme}
                          onClick={() => {
                            if (launch.externalLink) {
                              handleExternalLink(launch.externalLink);
                            } else if (launch.previewImages) {
                              handleOpenModal(launch.previewImages, launch.title);
                            }
                          }}
                        >
                          <Eye size={16} />
                          {launch.externalLink ? "Visit Website" : "Preview Report"}
                        </PreviewButton>
                      </Box>
                    ) : null}
                  </Box>

                  {/* Image Area - Bottom */}
                  <Box
                    component="img"
                    src={launch.image}
                    alt={launch.title}
                    sx={{
                      width: "60%",
                      marginLeft: "auto",
                      marginRight: "auto",
                      marginTop: "auto",
                      marginBottom: 0,
                      height: { xs: "250px", md: "300px" },
                      objectFit: "cover",
                      objectPosition: launch.title === "Allergy Report" ? "top" : "center",
                      borderTop: isDarkTheme 
                        ? "4px solid rgba(255, 255, 255, 0.35)" 
                        : "4px solid rgba(0, 0, 0, 0.25)",
                      borderLeft: isDarkTheme 
                        ? "4px solid rgba(255, 255, 255, 0.35)" 
                        : "4px solid rgba(0, 0, 0, 0.25)",
                      borderRight: isDarkTheme 
                        ? "4px solid rgba(255, 255, 255, 0.35)" 
                        : "4px solid rgba(0, 0, 0, 0.25)",
                      borderBottom: "none",
                      borderRadius: "20px 20px 0px 0px",
                      display: "block",
                      verticalAlign: "bottom",
                      lineHeight: 0,
                    }}
                  />
                </Card>
              );
            })}
          </Box>

          {/* Row 2: Narrow left, Wide right */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1.65fr" }, // ~38% / ~62%
              gap: { xs: "32px", md: "48px" },
            }}
          >
            {launches.slice(2, 4).map((launch, index) => {
              const isDarkTheme = launch.theme === "dark";
              const isPreNatalReport = launch.title === "Pre Natal Report";
              const isFoodIntoleranceReport = launch.title === "Food Intolerance Report";
              
              return (
                <Card
                  key={index + 2}
                  sx={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    backgroundColor: isDarkTheme ? "#022674" : "white",
                    display: "flex",
                    flexDirection: "column",
                    paddingBottom: 0,
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 48px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  {/* Card Content - Top */}
                  <Box
                    sx={{
                      padding: { xs: "24px", md: "32px" },
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      ...(!isDarkTheme && {
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='watermark' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M0 0h100v100H0z' fill='%23f8f9fa'/%3E%3Cpath d='M0 50 Q25 40, 50 50 T100 50' stroke='%23e2e8f0' stroke-width='0.5' fill='none' opacity='0.3'/%3E%3Cpath d='M0 30 Q25 20, 50 30 T100 30' stroke='%23e2e8f0' stroke-width='0.5' fill='none' opacity='0.2'/%3E%3Cpath d='M0 70 Q25 60, 50 70 T100 70' stroke='%23e2e8f0' stroke-width='0.5' fill='none' opacity='0.2'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23watermark)'/%3E%3C/svg%3E")`,
                          backgroundSize: "200px 200px",
                          opacity: 0.4,
                          pointerEvents: "none",
                          zIndex: 0,
                        },
                      }),
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "22px", md: "28px" },
                        fontWeight: 700,
                        fontFamily: "'Poppins', sans-serif",
                        color: isDarkTheme ? "#ffffff" : "#1e293b",
                        marginBottom: "12px",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {launch.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "14px", md: "16px" },
                        color: isDarkTheme ? "#ffffff" : "#64748b",
                        lineHeight: 1.6,
                        marginBottom: "24px",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {launch.description}
                    </Typography>

                    {/* Features List */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        position: "relative",
                        zIndex: 1,
                        marginBottom: (launch.previewImages || launch.externalLink) ? "20px" : 0,
                      }}
                    >
                      {launch.features.map((feature, featureIndex) => (
                        <Box
                          key={featureIndex}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "12px",
                          }}
                        >
                          {/* Light blue circular dot */}
                          <Box
                            sx={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: "#5BA3FF",
                              marginTop: "6px",
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: "14px", md: "16px" },
                              color: isDarkTheme ? "#ffffff" : "#1e293b",
                              lineHeight: 1.6,
                              fontWeight: 500,
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Preview Button - Show if previewImages or externalLink exist */}
                    {(launch.previewImages && launch.previewImages.length > 0) || launch.externalLink ? (
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          marginTop: "auto",
                          marginBottom: "16px",
                        }}
                      >
                        <PreviewButton
                          isDark={isDarkTheme}
                          onClick={() => {
                            if (launch.externalLink) {
                              handleExternalLink(launch.externalLink);
                            } else if (launch.previewImages) {
                              handleOpenModal(launch.previewImages, launch.title);
                            }
                          }}
                        >
                          <Eye size={16} />
                          {launch.externalLink ? "Visit Website" : "Preview Report"}
                        </PreviewButton>
                      </Box>
                    ) : null}
                  </Box>

                  {/* Image Area - Bottom */}
                  <Box
                    component="img"
                    src={launch.image}
                    alt={launch.title}
                    sx={{
                      width: "60%",
                      marginLeft: "auto",
                      marginRight: "auto",
                      marginTop: "auto",
                      marginBottom: 0,
                      height: { xs: "250px", md: "300px" },
                      objectFit: "cover",
                      objectPosition: (isPreNatalReport || isFoodIntoleranceReport) ? "top" : "center",
                      borderTop: isDarkTheme 
                        ? "4px solid rgba(255, 255, 255, 0.35)" 
                        : "4px solid rgba(0, 0, 0, 0.25)",
                      borderLeft: isDarkTheme 
                        ? "4px solid rgba(255, 255, 255, 0.35)" 
                        : "4px solid rgba(0, 0, 0, 0.25)",
                      borderRight: isDarkTheme 
                        ? "4px solid rgba(255, 255, 255, 0.35)" 
                        : "4px solid rgba(0, 0, 0, 0.25)",
                      borderBottom: "none",
                      borderRadius: "20px 20px 0px 0px",
                      display: "block",
                      verticalAlign: "bottom",
                      lineHeight: 0,
                    }}
                  />
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Gallery Modal for scrollable image preview */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="gallery-preview-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          sx={{
            width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
            maxWidth: "700px",
            maxHeight: { xs: "85vh", sm: "88vh", md: "90vh" },
            background: "#FFFFFF",
            borderRadius: { xs: "20px", md: "24px" },
            outline: "none",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 25px 80px rgba(0, 0, 0, 0.35)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: { xs: "16px 20px", md: "20px 28px" },
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                id="gallery-preview-modal"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "20px", sm: "24px" },
                  fontWeight: 700,
                  color: "#1D47A4",
                  letterSpacing: "-0.02em",
                }}
              >
                {currentTitle}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "12px", md: "13px" },
                  fontWeight: 500,
                  color: "#6B7280",
                  mt: 0.5,
                }}
              >
                {currentImages.length} {currentImages.length === 1 ? "page" : "pages"}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: "#4674D9",
                backgroundColor: "rgba(70, 116, 217, 0.08)",
                width: { xs: "40px", md: "44px" },
                height: { xs: "40px", md: "44px" },
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(70, 116, 217, 0.15)",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <X size={22} />
            </IconButton>
          </Box>

          {/* Scrollable Gallery Body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              padding: { xs: "16px", md: "24px" },
              background: "#F5F7FA",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#E8EDF5",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "linear-gradient(180deg, #4674D9 0%, #6E91EB 100%)",
                borderRadius: "4px",
                "&:hover": {
                  background: "linear-gradient(180deg, #3563C8 0%, #5D80DA 100%)",
                },
              },
            }}
          >
            {currentImages.map((image, index) => (
              <Box
                key={index}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                sx={{
                  background: "#FFFFFF",
                  borderRadius: { xs: "12px", md: "16px" },
                  padding: { xs: "12px", md: "16px" },
                  marginBottom: index < currentImages.length - 1 ? { xs: "16px", md: "20px" } : 0,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(70, 116, 217, 0.15)",
                  },
                }}
              >
                {/* Page Label */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "linear-gradient(135deg, #4674D9 0%, #6E91EB 100%)",
                    color: "#FFFFFF",
                    padding: { xs: "5px 12px", md: "6px 14px" },
                    borderRadius: "20px",
                    fontSize: { xs: "12px", md: "13px" },
                    fontWeight: 600,
                    fontFamily: "Inter, sans-serif",
                    marginBottom: { xs: "10px", md: "12px" },
                  }}
                >
                  Page {index + 1} of {currentImages.length}
                </Box>
                <Box
                  component="img"
                  src={image}
                  alt={`${currentTitle} - Page ${index + 1}`}
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: { xs: "8px", md: "12px" },
                    display: "block",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Scroll Indicator - only show if more than 1 page */}
          {currentImages.length > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: { xs: "10px 16px", md: "12px 20px" },
                background: "linear-gradient(180deg, #F8FAFF 0%, #FFFFFF 100%)",
                borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                color: "#6B7280",
                fontSize: { xs: "12px", md: "13px" },
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                flexShrink: 0,
                "& svg": {
                  animation: "bounce 1.5s infinite",
                },
                "@keyframes bounce": {
                  "0%, 100%": {
                    transform: "translateY(0)",
                  },
                  "50%": {
                    transform: "translateY(4px)",
                  },
                },
              }}
            >
              <ChevronDown size={18} />
              Scroll to view all pages
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default NewLaunchesSection;

